import React, { useEffect, useRef, useState } from "react";
import { cn } from "../../../../../shared/utils/cn";
import type { OrderSearchSuggestion } from "../../../../../domain/types/orders";

interface Props {
  value: string;
  onChange: (value: string) => void;
  fetchSuggestions: (keyword: string) => Promise<OrderSearchSuggestion[]>;
  className?: string;
}

const SUGGESTION_DELAY_MS = 200;
const TYPE_LABEL: Record<OrderSearchSuggestion["type"], string> = { CUSTOMER: "업체", CATEGORY: "종목" };

/** 주문 검색창. 입력하면 업체·종목 추천어를 보여주고, 고르면 검색어로 입력한다. */
export const OrderSearchInput: React.FC<Props> = ({ value, onChange, fetchSuggestions, className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [suggestions, setSuggestions] = useState<OrderSearchSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  // 추천어를 고른 직후에는 같은 값으로 다시 추천을 띄우지 않는다.
  const pickedValue = useRef<string | null>(null);

  useEffect(() => {
    const keyword = value.trim();
    if (!keyword || pickedValue.current === value) {
      setSuggestions([]);
      return;
    }
    let disposed = false;
    const timer = window.setTimeout(() => {
      fetchSuggestions(keyword)
        .then(result => {
          if (disposed) return;
          setSuggestions(result);
          setActiveIndex(-1);
        })
        .catch(() => { if (!disposed) setSuggestions([]); });
    }, SUGGESTION_DELAY_MS);
    return () => {
      disposed = true;
      window.clearTimeout(timer);
    };
  }, [value, fetchSuggestions]);

  useEffect(() => {
    const handlePointer = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handlePointer);
    return () => document.removeEventListener("mousedown", handlePointer);
  }, []);

  const pick = (suggestion: OrderSearchSuggestion) => {
    pickedValue.current = suggestion.value;
    onChange(suggestion.value);
    setOpen(false);
    setSuggestions([]);
  };

  const showList = open && suggestions.length > 0;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showList) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex(index => (index + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex(index => (index <= 0 ? suggestions.length - 1 : index - 1));
    } else if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      pick(suggestions[activeIndex]);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={cn("relative h-10", className)}>
      <img
        className="absolute left-3 top-1/2 -translate-y-1/2 w-[1rem] pointer-events-none opacity-70"
        alt=""
        src="/assets/icons/Search.svg"
      />
      <input
        type="text"
        role="combobox"
        aria-label="주문 검색"
        aria-expanded={showList}
        aria-controls="order-search-suggestions"
        aria-autocomplete="list"
        value={value}
        onChange={event => {
          pickedValue.current = null;
          onChange(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder="주문명, 업체, 종목, 이메일 검색"
        className="w-full h-full pl-9 pr-3 text-sm text-[#222222] placeholder:text-[#BBBBBB] bg-white border border-[#e0e0e0] rounded-[8px] focus:border-[#4285F4] focus:outline-none"
      />
      {showList && (
        <ul
          id="order-search-suggestions"
          role="listbox"
          aria-label="추천 검색어"
          className="absolute z-30 mt-1 w-full bg-white border border-[#e0e0e0] rounded-[8px] shadow-[0_8px_24px_rgba(0,0,0,0.08)] py-1 max-h-72 overflow-y-auto"
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={`${suggestion.type}-${suggestion.value}`}
              role="option"
              aria-selected={index === activeIndex}
              onMouseDown={event => {
                event.preventDefault();
                pick(suggestion);
              }}
              onMouseEnter={() => setActiveIndex(index)}
              className={cn(
                "flex items-center gap-2 px-3 py-2 text-sm cursor-pointer",
                index === activeIndex ? "bg-[#F5F8FF]" : "bg-white",
              )}
            >
              <span className="flex-shrink-0 px-1.5 py-0.5 rounded-[4px] text-[11px] font-medium bg-[#E8EFFF] text-[#4285F4]">
                {TYPE_LABEL[suggestion.type]}
              </span>
              <span className="truncate text-[#222222]">{suggestion.value}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
